import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalHeader, ModalBody, ModalFooterWithButtons } from '@evertrue/et-components';

/**
 * Generic confirmation modal for destructive or irreversible actions.
 * onConfirm may be async — the modal shows a busy state and always closes when done.
 * Error toasts should be raised inside onConfirm; the modal closes regardless.
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  title,
  body,
  onConfirm,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isDanger = false,
}) => {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="small">
      <ModalHeader title={title} closeModal={onClose} />
      <ModalBody>
        <p style={{ margin: 0 }}>{body}</p>
      </ModalBody>
      <ModalFooterWithButtons>
        <Button type="secondary" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button type={isDanger ? 'destroy' : 'action'} onClick={handleConfirm} disabled={busy}>
          {busy ? 'Working...' : confirmLabel}
        </Button>
      </ModalFooterWithButtons>
    </Modal>
  );
};

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string,
  // Override when 'Cancel' would be ambiguous next to the confirm label —
  // e.g. "Keep Scheduled" opposite "Cancel Send".
  cancelLabel: PropTypes.string,
  isDanger: PropTypes.bool,
};

export default ConfirmModal;
